import "./draggable.window.css"

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import useDialogAccessibility from "../../../hooks/useDialogAccessibility";

const DraggableModal = ({
  children,
  className = "",
  closeDisabled = false,
  headerText = null,
  initialFocusRef,
  isVisible,
  onClose,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef(null);
  const titleId = useId();
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  useDialogAccessibility({
    closeDisabled,
    containerRef: modalRef,
    initialFocusRef,
    isOpen: isVisible,
    onClose,
  });

  const centerModal = useCallback(() => {
    if (modalRef.current) {
      const modalWidth = modalRef.current.offsetWidth;
      const modalHeight = modalRef.current.offsetHeight;
      const newX = (window.innerWidth - modalWidth) / 2;
      const newY = (window.innerHeight - modalHeight) / 2;
      setPosition({ x: newX, y: newY });
    }
  }, []);

  useEffect(() => {
    if (isVisible) {
      centerModal();
    }
  }, [isVisible, centerModal]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newX = e.clientX - startPos.x;
      const newY = e.clientY - startPos.y;
      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, startPos]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!isVisible) return null;

  return (
    <div className="modal-overlay" onMouseDown={() => !closeDisabled && onClose()}>
      <div
        aria-labelledby={headerText ? titleId : undefined}
        aria-modal="true"
        className={`modal-content ${className}`}
        ref={modalRef}
        role="dialog"
        tabIndex="-1"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ top: `${position.y}px`, left: `${position.x}px` }}
      >
        <div className="modal-header" onMouseDown={handleMouseDown}>
          {headerText && <h2 className="modal-header-text" id={titleId}>{headerText}</h2>}
          <button
            aria-label={t("common.close")}
            className="close-button"
            disabled={closeDisabled}
            type="button"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={onClose}
          >
            &times;
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export default DraggableModal;
